import React from "react";
import QueryStatsOutlined from "@mui/icons-material/QueryStatsOutlined";
import ContactPageOutlined from "@mui/icons-material/ContactPageOutlined";
import BusinessCenterOutlined from "@mui/icons-material/BusinessCenterOutlined";
import CoPresentOutlined from "@mui/icons-material/CoPresentOutlined";
import EventAvailableOutlined from "@mui/icons-material/EventAvailableOutlined";
import PersonOutlineOutlined from "@mui/icons-material/PersonOutlineOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import CalendarViewMonthOutlined from "@mui/icons-material/CalendarViewMonthOutlined";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import LocalActivityOutlined from "@mui/icons-material/LocalActivityOutlined";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import MenuBookOutlined from "@mui/icons-material/MenuBookOutlined";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import DateRangeOutlined from "@mui/icons-material/DateRangeOutlined";
import EventOutlined from "@mui/icons-material/EventOutlined";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import ClassOutlined from "@mui/icons-material/ClassOutlined";
import SwapHorizOutlined from "@mui/icons-material/SwapHorizOutlined";
import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import PaymentsOutlined from "@mui/icons-material/PaymentsOutlined";
import SavingsOutlined from "@mui/icons-material/SavingsOutlined";
import TravelExploreOutlined from "@mui/icons-material/TravelExploreOutlined";
import CampaignOutlined from "@mui/icons-material/CampaignOutlined";
import BarChartOutlined from "@mui/icons-material/BarChartOutlined";
import TrendingUpOutlined from "@mui/icons-material/TrendingUpOutlined";
import HubOutlined from "@mui/icons-material/HubOutlined";
import WorkspacePremiumOutlined from "@mui/icons-material/WorkspacePremiumOutlined";
import RedeemOutlined from "@mui/icons-material/RedeemOutlined";
import FlagCircleOutlined from "@mui/icons-material/FlagCircleOutlined";
import TrackChangesOutlined from "@mui/icons-material/TrackChangesOutlined";
import CalculateOutlined from "@mui/icons-material/CalculateOutlined";
import PaidOutlined from "@mui/icons-material/PaidOutlined";
import Groups3Outlined from "@mui/icons-material/Groups3Outlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InboxOutlined from "@mui/icons-material/InboxOutlined";
import ManageAccountsOutlined from "@mui/icons-material/ManageAccountsOutlined";
import DnsOutlined from "@mui/icons-material/DnsOutlined";
import ModelTrainingOutlined from "@mui/icons-material/ModelTrainingOutlined";
import NotificationsActiveOutlined from "@mui/icons-material/NotificationsActiveOutlined";
import ForumOutlined from "@mui/icons-material/ForumOutlined";
import TableChartOutlined from "@mui/icons-material/TableChartOutlined";
import AssignmentTurnedInOutlined from "@mui/icons-material/AssignmentTurnedInOutlined";
import HowToRegOutlined from "@mui/icons-material/HowToRegOutlined";
import AppsOutlined from "@mui/icons-material/AppsOutlined";
import WidgetsOutlined from "@mui/icons-material/WidgetsOutlined";
import { APP_IDS } from "./routeAppMap";
import { getAppLabel } from "./appMeta";
import { isCustomAppId, toClientAppId } from "./customAppIds";
import { renderNavIcon } from "./muiNavIcons";

const R = {
  admin: "admin",
  manager: "Manager",
  counsellor: "Counsellor",
  visa: "Visa Counsellor",
  visaApp: "Visa&Application Counsellor",
  receptionist: "Receptionist",
  backOffice: "Back Office",
  lecturer: "Lecturer",
  accounts: "Accounts",
  accountant: "Accountant",
};

const CRM_DASHBOARD_ROLES = [R.admin, R.manager];
const COUNSELLOR_DASH_ROLES = [R.counsellor, R.visa, R.visaApp];
const TASKS_WIDE = [
  R.admin,
  R.manager,
  R.counsellor,
  R.visa,
  R.backOffice,
  R.visaApp,
  R.lecturer,
  R.accounts,
  R.accountant,
];

/** Approvals (sidebar + route) — not for reception, back office, counsellor types, or accounts. */
const APPROVAL_ROLES = [R.admin, R.manager, R.lecturer, R.accountant];

/** Calendar / sessions / classrooms — no counsellor types, no receptionist. */
const SCHEDULER_MAIN_ROLES = [R.admin, R.manager, R.backOffice, R.lecturer];
/** Session approvals — no back office. */
const SCHEDULER_APPROVAL_ACCESS_ROLES = [R.admin, R.manager, R.lecturer];
const RESCHEDULE_REQUEST_ROLES = [R.admin, R.manager];

function item(label, path, icon, roles, options = {}) {
  return { label, path, icon, roles, ...options };
}

function group(label, icon, roles, children) {
  return { group: true, label, icon, roles, children };
}

/** Per-app content only — Tasks, Approvals, and Apps launcher are added by `getComposedNavTree`. */
export const MENU_BY_APP = {
  [APP_IDS.LEADS]: [
    item("Overview", "/home", <QueryStatsOutlined />, CRM_DASHBOARD_ROLES),
    item("Counsellor dashboard", "/counsellor-dashboard", <CoPresentOutlined />, COUNSELLOR_DASH_ROLES),
    item("My enquiries", "/new-enquiry", <ContactPageOutlined />, COUNSELLOR_DASH_ROLES),
    item("Enquiry overview", "/all-enquiry", <ContactPageOutlined />, [
      R.admin,
      R.manager,
      R.receptionist,
      R.backOffice,
      R.accountant,
      R.accounts,
    ]),
    item("Back office dashboard", "/back-office-dashboard", <BusinessCenterOutlined />, [R.backOffice]),
    group(
      "Appointments",
      <EventAvailableOutlined />,
      [R.admin, R.counsellor, R.visa, R.receptionist, R.visaApp, R.manager],
      [
        item("My appointments", "/appointments/my", <PersonOutlineOutlined />, COUNSELLOR_DASH_ROLES),
        item("All appointments", "/appointments/all", <GroupsOutlined />, [R.admin, R.manager]),
        item("Calendar", "/appointments/calendar", <CalendarViewMonthOutlined />, [
          R.admin,
          R.counsellor,
          R.visa,
          R.receptionist,
          R.visaApp,
          R.manager,
        ]),
        item("Settings", "/appointments/settings", <TuneOutlined />, [
          R.admin,
          R.counsellor,
          R.visa,
          R.receptionist,
          R.visaApp,
          R.manager,
        ]),
      ]
    ),
    item("Exhibition", "/exhibition-enquiries", <LocalActivityOutlined />, [R.admin, R.receptionist]),
  ],

  [APP_IDS.ACADEMICS]: [
    item("Programs list", "/universities/list", <AccountTreeOutlined />, [R.admin, R.manager]),
    item("Course overview", "/programs/course-overview", <MenuBookOutlined />, [
      R.admin,
      R.receptionist,
      R.manager,
      R.backOffice,
      R.counsellor,
      R.visa,
      R.visaApp,
    ]),
    item("Analytics", "/programs/analytics", <BarChartOutlined />, [R.admin, R.manager]),
    item("Promotions", "/programs/promotions", <LocalOfferOutlined />, [R.admin, R.manager]),
    item("Program approval", "/universities/approval", <FactCheckOutlined />, [R.admin]),
  ],

  [APP_IDS.SCHEDULER]: [
    item("Calendar", "/schedule", <DateRangeOutlined />, SCHEDULER_MAIN_ROLES),
    item("Sessions", "/schedule/events", <EventOutlined />, SCHEDULER_MAIN_ROLES),
    item("Session approvals", "/schedule/approvals", <GavelOutlined />, SCHEDULER_APPROVAL_ACCESS_ROLES),
    item("Classrooms", "/schedule/classrooms", <ClassOutlined />, SCHEDULER_MAIN_ROLES),
    item("Reschedule requests", "/schedule/reschedule-requests", <SwapHorizOutlined />, RESCHEDULE_REQUEST_ROLES),
  ],

  [APP_IDS.FINANCE]: [
    item("Accounts dashboard", "/accounts-dashboard", <AccountBalanceOutlined />, [R.admin, R.accounts, R.manager, R.accountant]),
    item("Payments", "/accounts/payments", <PaymentsOutlined />, [R.admin, R.accounts, R.manager, R.accountant]),
    item("Finance settings", "/settings/finance", <SavingsOutlined />, [R.admin]),
  ],

  [APP_IDS.MARKETING]: [
    item("Marketing intelligence", "/marketing-intelligence", <TravelExploreOutlined />, [R.admin]),
    item("Campaigns", "/marketing-intelligence/campaigns", <CampaignOutlined />, [R.admin]),
    item("Campaign analytics", "/marketing-intelligence/analytics", <BarChartOutlined />, [R.admin]),
    item("Revenue analytics", "/marketing-intelligence/revenue", <TrendingUpOutlined />, [R.admin]),
    item("Mediums", "/marketing/revenue/mediums", <HubOutlined />, [R.admin]),
  ],

  [APP_IDS.PERFORMANCE]: [
    item("Incentives", "/incentives", <WorkspacePremiumOutlined />, [R.admin, R.manager, R.counsellor, R.visa, R.visaApp]),
    item("Bonuses", "/bonuses", <RedeemOutlined />, [R.admin, R.manager]),
    item("Bounties", "/bounties", <FlagCircleOutlined />, [R.admin, R.manager, R.counsellor, R.visa, R.visaApp]),
    item("Targets", "/targets", <TrackChangesOutlined />, [R.admin, R.manager, R.counsellor, R.visa, R.visaApp]),
    item("Commissions", "/commissions", <CalculateOutlined />, [R.admin, R.manager]),
    item("My commissions", "/my-commissions", <PaidOutlined />, [R.admin, R.manager, R.counsellor, R.visa, R.visaApp]),
    item("Counsellors (staff)", "/staff", <Groups3Outlined />, [R.admin, R.manager]),
  ],

  [APP_IDS.COMMUNICATION]: [
    item("Unified Inbox", "/unified-inbox", <InboxOutlined />, [R.admin]),
    item("WhatsApp inbox (legacy)", "/whatsapp-inbox", <WhatsAppIcon />, [R.admin]),
  ],

  [APP_IDS.ADMIN]: [
    item("Users", "/users", <ManageAccountsOutlined />, [R.admin]),
    item("App Builder", "/admin/app-builder", <WidgetsOutlined />, [R.admin]),
    item("Server logs", "/server-logs", <DnsOutlined />, [R.admin]),
    item("AI usage", "/ai-usage", <ModelTrainingOutlined />, [R.admin]),
    item("Notifications", "/notifications", <NotificationsActiveOutlined />, [R.admin]),
    item("AI assistant", "/ai-chat", <ForumOutlined />, [R.admin]),
  ],

  [APP_IDS.REPORTS]: [item("Reports", "/reports", <TableChartOutlined />, [R.admin])],
};

function isVisaCounsellorOnly(role) {
  return role === R.visa || role === R.visaApp;
}

export function getNavTreeForApp(appId, role) {
  if (isVisaCounsellorOnly(role) && appId === APP_IDS.LEADS) {
    return [];
  }

  const raw = MENU_BY_APP[appId] || [];
  const out = [];
  for (const entry of raw) {
    if (entry.group) {
      const children = (entry.children || []).filter((ch) => ch.roles?.includes(role));
      if (!children.length) continue;
      out.push({ ...entry, children });
    } else if (entry.roles?.includes(role)) {
      out.push(entry);
    }
  }
  return out;
}

function stripTasksApprovalsFromBase(base) {
  return base.filter((entry) => {
    if (entry.path && (entry.path === "/tasks" || entry.path === "/approvals")) return false;
    return true;
  });
}

const LAUNCHER_ENTRY = { launcher: true, label: "Apps", icon: <AppsOutlined /> };

function filterDynamicMenuItemsForRole(menuItems, role) {
  const n = (role || "").trim().toLowerCase();
  return (menuItems || []).filter((it) => {
    const ra = it.role_access;
    if (!ra || !ra.length || ra.includes("*")) return true;
    return ra.map((x) => String(x).toLowerCase()).includes(n);
  });
}

function buildDynamicNavBase(menuItems, role) {
  return filterDynamicMenuItemsForRole(menuItems, role)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((it) => ({
      label: it.label,
      path: it.path,
      icon: renderNavIcon(it.icon),
      roles: [role],
      dynamic: true,
    }));
}

/**
 * Every app: Tasks → Approvals (role permitting) → module links → Apps launcher.
 * For custom apps (`ca_*`), pass `options.dynamicMenuItems` from the nav API.
 */
export function getComposedNavTree(appId, role, options = {}) {
  const { dynamicMenuItems } = options;

  if (isCustomAppId(appId) && Array.isArray(dynamicMenuItems)) {
    const base = buildDynamicNavBase(dynamicMenuItems, role);
    const head = [];
    if (TASKS_WIDE.includes(role)) {
      head.push(item("Tasks", "/tasks", <AssignmentTurnedInOutlined />, TASKS_WIDE));
    }
    if (APPROVAL_ROLES.includes(role)) {
      head.push(item("Approvals", "/approvals", <HowToRegOutlined />, APPROVAL_ROLES));
    }
    return [...head, ...base, LAUNCHER_ENTRY];
  }

  const base = stripTasksApprovalsFromBase(getNavTreeForApp(appId, role));
  const head = [];

  if (TASKS_WIDE.includes(role)) {
    head.push(item("Tasks", "/tasks", <AssignmentTurnedInOutlined />, TASKS_WIDE));
  }
  if (APPROVAL_ROLES.includes(role)) {
    head.push(item("Approvals", "/approvals", <HowToRegOutlined />, APPROVAL_ROLES));
  }

  return [...head, ...base, LAUNCHER_ENTRY];
}

export function getVisibleAppIds(role) {
  if (!role) return [];
  return Object.values(APP_IDS).filter((appId) => {
    if (getNavTreeForApp(appId, role).length > 0) return true;
    if (appId === APP_IDS.LEADS && isVisaCounsellorOnly(role)) return true;
    return false;
  });
}

export function getDefaultPathForApp(appId, role, options = {}) {
  const { dynamicMenuItems } = options;
  if (isCustomAppId(appId) && dynamicMenuItems?.length) {
    const sorted = filterDynamicMenuItemsForRole(dynamicMenuItems, role).sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    return sorted[0]?.path || "/home";
  }

  const tree = getComposedNavTree(appId, role, options).filter((e) => !e.launcher);
  if (!tree.length) return "/home";
  const moduleOnly = tree.filter((e) => !e.path || (e.path !== "/tasks" && e.path !== "/approvals"));
  const source = moduleOnly.length ? moduleOnly : tree;
  const first = source[0];
  if (first.group && first.children?.length) return first.children[0].path;
  return first.path || "/home";
}

export function buildGlobalSearchIndex(role, options = {}) {
  const { customApps = [] } = options;
  const apps = getVisibleAppIds(role);
  const rows = [];
  for (const appId of apps) {
    const tree = getComposedNavTree(appId, role).filter((e) => !e.launcher);
    const walk = (nodes) => {
      for (const n of nodes) {
        if (n.launcher) continue;
        if (n.group) walk(n.children || []);
        else if (n.path) {
          rows.push({ label: n.label, path: n.path, appId, appLabel: getAppLabel(appId) });
        }
      }
    };
    walk(tree);
  }
  for (const app of customApps) {
    if (app.is_system) continue;
    const cid = toClientAppId(app.id);
    const appLabel = app.name || cid;
    for (const it of app.menu_items || []) {
      rows.push({ label: it.label, path: it.path, appId: cid, appLabel });
    }
  }
  return rows;
}
